import React, { useEffect, useState } from 'react';
import { Check, LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import useElementPageTop from '@/lib/hooks/useElementPageTop';
import { useTranslation } from 'next-i18next';
import styles from './searching.module.less';

type ListItem = {
  status: 'success' | 'loading' | 'waiting';
  text: string;
};

const scoreMap = {
  waiting: 0,
  loading: 0,
  success: 16,
};

export default function Searching({ className }: { className?: string }) {
  const { ref: sectionRef, top } = useElementPageTop<HTMLElement>();
  const { t } = useTranslation();

  const [list, setList] = useState<ListItem[]>([
    { text: t('Reviewing content...'), status: 'waiting' },
    { text: t('Detecting bots...'), status: 'waiting' },
    { text: t('Evaluating shadowban risk...'), status: 'waiting' },
    { text: t('Searching for impersomators...'), status: 'waiting' },
    { text: t('Analyzing account activity...'), status: 'waiting' },
    { text: t('Calculating your score...'), status: 'waiting' },
  ]);
  const [processRatio, setProcessRatio] = useState(0);

  useEffect(() => {
    if (list[0].status === 'waiting') {
      const newList = [...list];
      newList[0].status = 'loading';
      setList(newList);
      setProcessRatio(0);
      return;
    }
    const tid = setTimeout(
      () => {
        if (list.at(-1).status === 'loading') return;
        const newList = [...list];
        const loadingIndex = newList.findIndex((item) => item.status === 'loading');
        if (loadingIndex !== -1) {
          newList[loadingIndex].status = 'success';
          if (loadingIndex + 1 < newList.length) {
            newList[loadingIndex + 1].status = 'loading';
          }
        } else {
          const waitingIndex = newList.findIndex((item) => item.status === 'waiting');
          if (waitingIndex !== -1) {
            newList[waitingIndex].status = 'loading';
          }
        }

        const newProcess = newList.reduce((acc, item) => acc + (scoreMap[item.status] || 0), 0);

        setList(newList);
        setProcessRatio(newProcess);
      },
      // 1.5 ~ 2.0 秒
      Math.random() * 500 + 1500
    );

    return () => {
      clearTimeout(tid);
    };
  }, [list]);

  useEffect(() => {
    if (top) {
      console.log(top);
      const isMobile = window.innerWidth <= 768;
      const offset = isMobile ? 330 : 350;
      scrollTo({
        top: top - offset,
        behavior: 'smooth',
      });
    }
  }, [top]);

  return (
    <section ref={sectionRef} className={styles.section}>
      <div className={cn(styles.container, className)}>
        <div className={styles.percentage}>
          {processRatio}
          <span className={styles.percentSymbol}>%</span>
        </div>
        <div className={styles.progressBarWrapper}>
          <div className={styles.progressBar} style={{ width: `${processRatio}%` }} />
        </div>
        <div className={styles.listContainer}>
          {list.map((item) => (
            <div className={styles.listItem} key={item.text}>
              {item.status === 'success' && <Check size={28} className={styles.iconSuccess} />}
              {item.status === 'loading' && (
                <LoaderCircle size={28} className={styles.iconLoading} />
              )}
              {item.status === 'waiting' && <div className={styles.iconPlaceholder} />}
              <div className={cn(
                styles.itemText,
                item.status === 'success' ? styles.completed : styles.pending
              )}>
                {item.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
